import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Switch,
    IconButton,
    TextField,
    InputAdornment,
    TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { colors } from "components/Theme/WebTheme";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";

// Common cell style
const cellStyle = {
    verticalAlign: "top",
    paddingTop: 2,
    paddingBottom: 2,
};

const DocumentFieldTable = ({
    documents,
    onEditDocument,
    onToggleState,
    onToggleRequired,
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);

    // Filter documents based on search query
    const filteredDocuments = documents.filter((doc) =>
        doc.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Get paginated documents
    const paginatedDocuments = filteredDocuments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box>
            {/* Search bar moved outside TableContainer */}
            <TextField
                fullWidth
                variant="outlined"
                placeholder={t("search_by_document_name")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    backgroundColor: colors.WHITE,
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.MAIN_COLOR }}>
                            <TableCell sx={cellStyle}>
                                {t("document")}
                            </TableCell>
                            <TableCell sx={cellStyle}>
                                {t("internal_name")}
                            </TableCell>
                            <TableCell sx={cellStyle}>
                                {t("entry_type")}
                            </TableCell>
                            <TableCell sx={cellStyle}>{t("to")}</TableCell>
                            <TableCell sx={cellStyle}>
                                {t("required")}
                            </TableCell>
                            <TableCell sx={cellStyle}>{t("state")}</TableCell>
                            <TableCell
                                sx={{ ...cellStyle, textAlign: "center" }}
                            >
                                {t("actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedDocuments.map((doc) => {
                            const forUser = doc.for_user;
                            const isInvalid = !(
                                forUser === "both" ||
                                forUser === "driver" ||
                                forUser === "customer"
                            );

                            return (
                                <TableRow key={doc.internal_name}>
                                    <TableCell sx={cellStyle}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: "medium" }}
                                        >
                                            {doc.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {doc.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        {doc.internal_name}
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        <Typography variant="body2">
                                            {doc.entry_name}
                                            <br />
                                            <span
                                                style={{
                                                    color: colors.SECONDARY,
                                                    fontSize: 12,
                                                    fontStyle: "italic",
                                                }}
                                            >
                                                {doc.allowed_format_type.join(
                                                    ", "
                                                )}
                                                {` (max. ${doc.max_size}MB)`}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        {t(doc.for_user)}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            cellStyle,
                                            textAlign: "center",
                                            alignContent: "start",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Switch
                                            checked={doc.required}
                                            disabled={isInvalid || !doc.state}
                                            onChange={() =>
                                                onToggleRequired?.(doc)
                                            }
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Switch
                                                checked={doc.state}
                                                disabled={isInvalid}
                                                onChange={() =>
                                                    onToggleState?.(doc)
                                                }
                                                color="primary"
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...cellStyle,
                                            textAlign: "center",
                                        }}
                                    >
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() =>
                                                onEditDocument?.(doc)
                                            }
                                            disabled={
                                                isInvalid || !onEditDocument
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredDocuments.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10]}
                />
            </TableContainer>
        </Box>
    );
};

// PropTypes for type checking
DocumentFieldTable.propTypes = {
    documents: PropTypes.arrayOf(
        PropTypes.shape({
            internal_name: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            entry_type: PropTypes.string.isRequired,
            allowed_format_type: PropTypes.arrayOf(PropTypes.string).isRequired,
            max_size: PropTypes.number.isRequired,
            for_user: PropTypes.string,
            requested: PropTypes.bool.isRequired,
            state: PropTypes.bool.isRequired,
        })
    ).isRequired,
    onEditDocument: PropTypes.func,
    onToggleState: PropTypes.func,
};

// Default props
DocumentFieldTable.defaultProps = {
    onEditDocument: undefined,
    onToggleState: undefined,
};

export default DocumentFieldTable;
